import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/common/DataTable/DataTable';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useLocations,
  useLocationsDropdown,
  useCreateLocation,
  useUpdateLocation,
  useDeleteLocation,
  invalidParentIds,
  Location,
} from '@/api/hooks/useLocations';
import { useFeature } from '@/contexts/FeatureFlagContext';
import {
  Plus,
  Search,
  Filter,
  MapPin,
  Building2,
  Warehouse,
  Truck,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
  Package,
  CornerDownRight,
} from 'lucide-react';

export default function LocationsPage() {
  const { t } = useTranslation(['catalogs', 'common']);
  const showLocationType = useFeature('clients'); // Location types are Toca-specific

  // Location type config for badges/icons
  const locationTypeConfig: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
    BACKOFFICE: {
      label: t('locations.types.backoffice'),
      icon: <Building2 className="h-3 w-3" />,
      className: 'bg-purple-100 text-purple-700',
    },
    CLIENT: {
      label: t('locations.types.client'),
      icon: <MapPin className="h-3 w-3" />,
      className: 'bg-blue-100 text-blue-700',
    },
    TRANSFER: {
      label: t('locations.types.transfer'),
      icon: <Truck className="h-3 w-3" />,
      className: 'bg-orange-100 text-orange-700',
    },
  };

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Dialog state
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [deleteLocation, setDeleteLocation] = useState<Location | null>(null);

  // Form state
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    type?: string;
    address?: string;
    parentLocationId: number | null;
  }>({
    code: '',
    name: '',
    type: '',
    address: '',
    parentLocationId: null,
  });

  // F1 — full list for the parent dropdown (self + descendants excluded)
  const { data: allLocations } = useLocationsDropdown();
  const parentOptions = useMemo(() => {
    const list = (allLocations ?? []).filter((l) => l.isActive);
    if (!editingLocation) return list;
    const excluded = invalidParentIds(list, editingLocation.id);
    return list.filter((l) => !excluded.has(l.id));
  }, [allLocations, editingLocation]);

  // Fetch locations from API
  const {
    data: locationsResponse,
    isLoading,
    error,
  } = useLocations({
    search: searchTerm || undefined,
    type: typeFilter || undefined,
    isActive: statusFilter ? statusFilter === 'active' : undefined,
    page,
    pageSize,
  });

  // Mutations
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const deleteMutation = useDeleteLocation();

  // Calculate stats
  const stats = useMemo(() => {
    const items = locationsResponse?.items ?? [];
    return {
      total: locationsResponse?.totalCount ?? 0,
      active: items.filter((l) => l.isActive).length,
      totalAssets: items.reduce((sum, l) => sum + (l.assetCount || 0), 0),
    };
  }, [locationsResponse]);

  // Handlers
  const handleOpenDialog = (location?: Location) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        code: location.code,
        name: location.name,
        type: location.type || '',
        address: location.address || '',
        parentLocationId: location.parentLocationId,
      });
    } else {
      setEditingLocation(null);
      setFormData({
        code: '',
        name: '',
        type: '',
        address: '',
        parentLocationId: null,
      });
    }
    setShowLocationDialog(true);
  };

  const handleCloseDialog = () => {
    setShowLocationDialog(false);
    setEditingLocation(null);
    setFormData({ code: '', name: '', type: '', address: '', parentLocationId: null });
  };

  const handleSubmit = async () => {
    try {
      if (editingLocation) {
        await updateMutation.mutateAsync({
          id: editingLocation.id,
          data: {
            code: formData.code,
            name: formData.name,
            type: formData.type || null,
            address: formData.address || null,
            parentLocationId: formData.parentLocationId,
          },
        });
        toast.success(t('locations.messages.updated'));
      } else {
        await createMutation.mutateAsync({
          code: formData.code,
          name: formData.name,
          type: formData.type || undefined,
          address: formData.address || undefined,
          parentLocationId: formData.parentLocationId,
        });
        toast.success(t('locations.messages.created'));
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save location:', error);
      toast.error(t('locations.messages.errorSaving'));
    }
  };

  const handleDelete = async () => {
    if (!deleteLocation) return;
    try {
      await deleteMutation.mutateAsync(deleteLocation.id);
      toast.success(t('locations.messages.deleted'));
      setDeleteLocation(null);
    } catch (error) {
      console.error('Failed to delete location:', error);
      toast.error(t('locations.messages.errorDeleting'));
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const hasActiveFilters = searchTerm || typeFilter || statusFilter;

  // Table columns
  const columns = useMemo<ColumnDef<Location>[]>(() => {
    const cols: ColumnDef<Location>[] = [
      {
        accessorKey: 'code',
        header: t('locations.table.code'),
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.original.code}</span>
        ),
      },
      {
        accessorKey: 'name',
        header: t('locations.table.name'),
        cell: ({ row }) => (
          // F1 — indentation renders the hierarchy when the list is in tree order
          <div
            className="font-medium flex items-center gap-1"
            style={{ paddingLeft: row.original.depth * 16 }}
          >
            {row.original.depth > 0 && (
              <CornerDownRight className="h-3 w-3 text-muted-foreground shrink-0" />
            )}
            {row.original.name}
          </div>
        ),
      },
      {
        accessorKey: 'parentName',
        header: t('locations.table.parent'),
        cell: ({ row }) =>
          row.original.parentName ?? <span className="text-muted-foreground">-</span>,
      },
    ];

    // Add type column only for Toca
    if (showLocationType) {
      cols.push({
        accessorKey: 'type',
        header: t('locations.table.type'),
        cell: ({ row }) => {
          const type = row.original.type;
          if (!type) return <span className="text-muted-foreground">-</span>;
          const config = locationTypeConfig[type];
          if (!config) return type;
          return (
            <Badge className={`gap-1 ${config.className}`}>
              {config.icon}
              {config.label}
            </Badge>
          );
        },
      });
    }

    cols.push(
      {
        accessorKey: 'address',
        header: t('locations.table.address'),
        cell: ({ row }) => row.original.address || '-',
      },
      {
        accessorKey: 'assetCount',
        header: t('locations.table.assets'),
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Package className="h-4 w-4 text-muted-foreground" />
            {row.original.assetCount}
          </div>
        ),
      },
      {
        accessorKey: 'isActive',
        header: t('locations.table.status'),
        cell: ({ row }) => (
          <Badge
            className={
              row.original.isActive
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          >
            {row.original.isActive ? t('locations.status.active') : t('locations.status.inactive')}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('locations.table.actions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleOpenDialog(row.original)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('locations.actions.edit')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteLocation(row.original)}
                className="text-destructive focus:text-destructive"
                disabled={row.original.assetCount > 0}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('locations.actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      }
    );

    return cols;
  }, [showLocationType, t]);

  // Pagination handlers
  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const isFormValid = formData.code && formData.name;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('locations.pageTitle')}</h1>
          <p className="text-muted-foreground">{t('locations.pageDescription')}</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="mr-2 h-4 w-4" />
          {t('locations.newLocation')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('locations.stats.total')}</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.total}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('locations.stats.active')}</CardTitle>
            <Warehouse className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.active}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('locations.stats.totalAssets')}</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.totalAssets}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Location List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('locations.cardTitle')}</CardTitle>
          <CardDescription>{t('locations.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Controls */}
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('locations.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="pl-8"
                />
              </div>
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {t('common:buttons.filters')}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2">
                    {t('common:status.active')}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="flex flex-wrap items-center gap-4 p-4 rounded-lg border bg-muted/50">
                {showLocationType && (
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm font-medium mb-1.5 block">{t('locations.filters.type')}</label>
                    <Select
                      value={typeFilter || 'all'}
                      onValueChange={(v) => {
                        setTypeFilter(v === 'all' ? '' : v);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('locations.types.all')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('locations.types.all')}</SelectItem>
                        <SelectItem value="BACKOFFICE">{t('locations.types.backoffice')}</SelectItem>
                        <SelectItem value="CLIENT">{t('locations.types.client')}</SelectItem>
                        <SelectItem value="TRANSFER">{t('locations.types.transfer')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('locations.filters.status')}</label>
                  <Select
                    value={statusFilter || 'all'}
                    onValueChange={(v) => {
                      setStatusFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('locations.status.all')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('locations.status.all')}</SelectItem>
                      <SelectItem value="active">{t('locations.status.active')}</SelectItem>
                      <SelectItem value="inactive">{t('locations.status.inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    {t('locations.filters.clearFilters')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Data Table */}
          {error ? (
            <div className="text-center py-8 text-destructive">
              {t('locations.messages.errorLoading')}
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={locationsResponse?.items ?? []}
              pageCount={locationsResponse?.totalPages ?? 1}
              pageIndex={page - 1}
              pageSize={pageSize}
              onPageChange={(pageIndex) => handlePageChange(pageIndex + 1)}
              onPageSizeChange={handlePageSizeChange}
              totalItems={locationsResponse?.totalCount ?? 0}
            />
          )}
        </CardContent>
      </Card>

      {/* Location Form Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingLocation ? t('locations.form.editTitle') : t('locations.form.createTitle')}</DialogTitle>
            <DialogDescription>
              {editingLocation
                ? t('locations.form.editDescription')
                : t('locations.form.createDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="code">{t('locations.form.codeLabel')}</Label>
              <Input
                id="code"
                placeholder={t('locations.form.codePlaceholder')}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="name">{t('locations.form.nameLabel')}</Label>
              <Input
                id="name"
                placeholder={t('locations.form.namePlaceholder')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            {showLocationType && (
              <div>
                <Label htmlFor="type">{t('locations.form.typeLabel')}</Label>
                <Select
                  value={formData.type || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, type: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('locations.form.typePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('locations.types.noType')}</SelectItem>
                    <SelectItem value="BACKOFFICE">{t('locations.types.backoffice')}</SelectItem>
                    <SelectItem value="CLIENT">{t('locations.types.client')}</SelectItem>
                    <SelectItem value="TRANSFER">{t('locations.types.transfer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="parentLocation">{t('locations.form.parentLabel')}</Label>
              <Select
                value={formData.parentLocationId != null ? String(formData.parentLocationId) : 'none'}
                onValueChange={(v) =>
                  setFormData({ ...formData, parentLocationId: v === 'none' ? null : Number(v) })
                }
              >
                <SelectTrigger id="parentLocation">
                  <SelectValue placeholder={t('locations.form.parentNone')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('locations.form.parentNone')}</SelectItem>
                  {parentOptions.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="address">{t('locations.form.addressLabel')}</Label>
              <Input
                id="address"
                placeholder={t('locations.form.addressPlaceholder')}
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? t('common:status.saving')
                : editingLocation
                ? t('common:buttons.saveChanges')
                : t('common:buttons.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteLocation}
        onOpenChange={(open) => !open && setDeleteLocation(null)}
        title={t('locations.dialog.deleteTitle')}
        description={
          deleteLocation?.assetCount && deleteLocation.assetCount > 0
            ? t('locations.dialog.cannotDelete', { name: deleteLocation?.name, count: deleteLocation.assetCount })
            : t('locations.dialog.deleteConfirm', { name: deleteLocation?.name })
        }
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
